import React from "react"
import { Dialog, FlatButton, Chip, Avatar, SelectField, MenuItem } from "material-ui"
import strings from "../../../localization/strings"
import { grey800 } from "material-ui/styles/colors"
import { IEquipmentDocument, IJobDocument, IJobPhase, IPosition, IPunchBundle, IPunchDocument, IPunchShortDocument } from "../../../models"
import AccountStore from "../../../stores/AccountStore"
import HierarchyItemChip from "../../hierarchy/HierarchyItemChip"
import JobsChip from "../../job/JobsChip"
import PhasesChip from "../../phase/PhasesChip"
import PositionSelector from "../../position/PositionSelector"
import PayClassSelector from "../../position/pay_class/PayClassSelector"
import ImageLoader from "../../misc/ImageLoader"
import { checkJobFieldAvailable } from "../../../helpers/jobHelper"

interface IProps {
    originalCommonGlobalPunchData: IPunchShortDocument
    selectedPunchBundles: IPunchBundle[]
    hierarchyLevelId: string
    positionId: string
    payClassId: string
    equipment: IEquipmentDocument
    payTypeId: string
    job: IJobDocument
    phase: IJobPhase
    punchPosition: IPosition
    handleClose(): void
    handleEditGlobalPunches(punchBundles: IPunchBundle[]): void
}

const GlobalPunchEditReviewDialog: React.SFC<IProps> = (props: IProps) => {
    const hierarchyChanged = props.hierarchyLevelId !== props.originalCommonGlobalPunchData.HierarchyItemId
    const jobId = props.job ? props.job.Id : null
    const jobChanged = jobId !== props.originalCommonGlobalPunchData.JobId
    const phaseId = props.phase ? props.phase.Id : null
    const phaseChanged = phaseId !== props.originalCommonGlobalPunchData.PhaseId
    const positionChanged = props.positionId !== props.originalCommonGlobalPunchData.PositionId
    const payClassChanged = props.payClassId !== props.originalCommonGlobalPunchData.PayClassId
    const payTypeChanged = props.payTypeId !== AccountStore.DiffPayTypeId && props.payTypeId !== props.originalCommonGlobalPunchData.PayTypeId
    const equipmentId = props.equipment ? props.equipment.Id : null
    const equipmentChanged = equipmentId !== props.originalCommonGlobalPunchData.EquipmentId
    const changed = hierarchyChanged || jobChanged || phaseChanged || positionChanged || payClassChanged || payTypeChanged || equipmentChanged
    const handleEditGlobalPunches = () => {
        const punchBundles = props.selectedPunchBundles.map(punchBundle => {
            const punch = punchBundle.Punch
            const updatePunch = {
                Id: punch.Id,
                HierarchyItemId: punch.HierarchyItemId,
                PositionId: punch.PositionId,
                PayClassId: punch.PayClassId,
                EquipmentId: punch.EquipmentId,
                PayTypeId: punch.PayTypeId,
                JobId: punch.JobId,
                JobName: punch.JobName,
                JobCostCode: punch.JobCostCode,
                PhaseId: punch.PhaseId,
                PhaseName: punch.PhaseName,
                PhaseCostCode: punch.PhaseCostCode,
                DayGroupingTimeLocal: punch.DayGroupingTimeLocal,
                TicksSinceChange: punch.TicksSinceChange
            } as IPunchDocument
            if (hierarchyChanged || jobChanged || phaseChanged) {
                updatePunch.HierarchyItemId = props.hierarchyLevelId
                updatePunch.JobId = props.job ? props.job.Id : null
                updatePunch.JobName = props.job ? props.job.Name : null
                updatePunch.JobCostCode = props.job ? props.job.CostCode : null
                updatePunch.PhaseId = (props.job && props.phase) ? props.phase.Id : null
                updatePunch.PhaseName = (props.job && props.phase) ? props.phase.Name : null
                updatePunch.PhaseCostCode = (props.job && props.phase) ? props.phase.CostCode : null
            }
            if (positionChanged) {
                updatePunch.PositionId = props.positionId
            }
            if (payClassChanged) {
                updatePunch.PayClassId = props.payClassId
            }
            if (payTypeChanged) {
                updatePunch.PayTypeId = props.payTypeId
            }
            if (equipmentChanged && !punch.BreakId && !punch.MobileBreakId) {
                updatePunch.EquipmentId = props.equipment ? props.equipment.Id : null
            }
            return {
                Punch: updatePunch,
                PunchPosition: positionChanged ? { Position: props.punchPosition } : punchBundle.PunchPosition
            }
        })

        props.handleEditGlobalPunches(punchBundles)
    }

    return (
        <Dialog
            open
            title={strings.Final_Review}
            actions={[
                <FlatButton key="Cancel" label={strings.Cancel} secondary onClick={props.handleClose} />,
                <FlatButton key="Punch" label={strings.Edit_Punches} disabled={!changed} primary onClick={handleEditGlobalPunches} />
            ]}
        >
            {changed ?
                <div>
                    <div style={{ color: grey800 }}>
                        {strings.formatString(strings.You_are_editing_x_punches_to_have_the_following_properties, props.selectedPunchBundles.length)}
                    </div>
                    {(hierarchyChanged || jobChanged || phaseChanged) &&
                        <div style={{ marginTop: 15 }}>
                            <div style={{ display: "flex" }}>
                                <div style={{ flex: 1, display: "flex" }}>
                                    <i className="fa fa-map-marker" style={{ marginRight: 10 }} />
                                    <HierarchyItemChip
                                        title={strings.Hierarchy}
                                        hierarchyItemId={props.hierarchyLevelId}
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    {props.job &&
                                        <JobsChip
                                            disabled
                                            label={checkJobFieldAvailable('singular') ? `${AccountStore.getCurrentAccountSettings().CustomFieldSettings.JobFieldLabels.SingularJobLabel}` : strings.Job}
                                            filterHierarchyId={props.hierarchyLevelId}
                                            selectedJobs={[props.job]}
                                            restrictHierarchy
                                            handleSave={() => { }}
                                        />}
                                </div>
                                <div style={{ flex: 1 }}>
                                    {props.phase &&
                                        <PhasesChip
                                            disabled
                                            label={strings.Phase}
                                            phases={props.job.Phases}
                                            selectedPhase={props.phase}
                                            handleSave={() => { }}
                                        />
                                    }
                                </div>
                            </div >
                        </div>
                    }
                    {positionChanged &&
                        <div style={{ display: "flex" }}>
                            <div style={{ float: "left", paddingTop: 40 }}>
                                <i className="fa fa-user" style={{ marginRight: 10 }} />
                            </div>
                            <div style={{ float: "left" }}>
                                <PositionSelector
                                    positionId={props.positionId}
                                    positions={[props.punchPosition]}
                                    label={strings.Position}
                                    onSave={() => { }}
                                    disabled
                                />
                            </div>
                            <div style={{ clear: "both" }} />
                        </div>
                    }
                    {payClassChanged &&
                        <div style={{ display: "flex" }}>
                            <i className="fa fa-tag" style={{ marginRight: 10, marginTop: 40 }} />
                            <PayClassSelector
                                readonly
                                defaultPayClass={props.punchPosition && props.punchPosition.DefaultPayClass}
                                availablePayClasses={props.punchPosition && props.punchPosition.AvailablePayClasses}
                                selectedPayClassId={props.payClassId}
                                handleChange={() => { }}
                            />
                        </div>
                    }
                    {payTypeChanged &&
                        <div style={{ display: "flex" }}>
                            <i className="fa fa-tag" style={{ marginRight: 10, marginTop: 40 }} />
                            <SelectField
                                floatingLabelText={strings.Pay_Type}
                                value={props.payTypeId}
                                disabled
                            >
                                {AccountStore.getCurrentAccount().PayTypes.map((payType, index) =>
                                    <MenuItem value={payType.Id} primaryText={payType.Name} key={index} />
                                )}
                            </SelectField>
                        </div>
                    }
                    {equipmentChanged &&
                        <div style={{ display: "inline-block" }}>
                            <i className="fa fa-wrench" style={{ marginRight: 10 }} />
                            <div style={{ marginTop: 10, marginBottom: 10, display: "inline-block" }}>
                                <div style={{ fontSize: 12, color: "rgba(0, 0, 0, 0.298)" }}>{strings.Equipment}</div>
                                <Chip>
                                    {props.equipment ?
                                        <div>
                                            {props.equipment.ImageS3Url &&
                                                <Avatar style={styles.avatar}>
                                                    <ImageLoader url={props.equipment.ImageS3Url} fixedHeight={75} avatar={true} />
                                                </Avatar>
                                            }
                                            {props.equipment.Name}
                                            {props.equipment.ExternalId && " - " + props.equipment.ExternalId}
                                        </div> :
                                        <div>
                                            {strings.None}
                                        </div>
                                    }

                                </Chip>
                            </div>
                        </div>
                    }
                    <div style={{ marginTop: 20 }}>
                        <span style={{ color: grey800 }}>{`${strings.Note}:`}</span>
                        <span style={{ marginLeft: 5 }}>{strings.If_a_punch_is_edited_with_a_new_item_the_associated_punch_in_the_punchset_will_be_adjusted_as_well}</span>
                    </div>
                </div> :
                <div>
                    {strings.No_changes_were_made}
                </div>
            }
        </Dialog>
    )
}

export default GlobalPunchEditReviewDialog

const styles = {
    chip: {
        display: "inline-block",
        cursor: "pointer",
        marginTop: 4
    },
    avatar: {
        width: 37,
        height: 37,
        marginTop: -8,
        position: "inherit",
        top: 11
    },
    chipLabel: {
        position: "inherit",
        top: -3
    }
}